import React, { useEffect, useState } from 'react';
import { AlertTriangle, Mail, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Red banner at the top of the Admin Panel that surfaces a failing outbound-
 * email configuration. Silent email failure (Resend domain unverified, sandbox
 * sender, missing API key) affects welcome/reset/notification emails for every
 * non-admin user — and is invisible without this banner.
 */
const EmailHealthBanner = () => {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API_URL}/api/admin/email-health`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) setHealth(await res.json());
      } catch (healthErr) {
        /* swallow — banner is a nice-to-have */
        console.debug('Email health check failed:', healthErr?.message);
      }
    };
    load();
  }, []);

  if (!health || health.healthy || dismissed) return null;

  return (
    <div
      data-testid="email-health-banner"
      className="bg-red-900/40 border border-red-500/50 rounded-xl p-4 mb-4 flex items-start gap-3"
    >
      <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="h-4 w-4 text-red-400" />
          <h3 className="text-red-300 font-semibold text-sm">
            Outbound email is NOT being delivered
          </h3>
        </div>
        <ul className="text-red-200/90 text-xs space-y-0.5 list-disc list-inside">
          {health.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <p className="text-red-200/70 text-xs mt-2">
          Current sender: <span className="font-mono">{health.sender_email}</span>
          {health.recent_failures_today > 0 && (
            <> &middot; <span>{health.recent_failures_today} failures logged today</span></>
          )}
        </p>
      </div>
      <button
        onClick={() => setDismissed(true)}
        data-testid="dismiss-email-health-banner"
        className="text-red-400 hover:text-red-200"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default EmailHealthBanner;
